import { costCents, AI_TASKS, type AiTask } from './aiModels';
import type { MagicItem } from './magic';

/**
 * Keeping the AI good and cheap (1.27, Mike 26.10.: "wenn sie … 2–3 Anläufe braucht, bringt sie
 * keinen Vorteil"): the built-in test sentences for the admin's test set and the summary of the
 * measuring (aiCalls). Pure – the server and the admin page share it.
 */

/** The family the test sentences talk about – the demo family, never a real one. */
export const TEST_FAMILY = [
  { name: 'Mama', kid: false },
  { name: 'Papa', kid: false },
  { name: 'Jonas', kid: true },
  { name: 'Lotta', kid: true },
  { name: 'Ben', kid: true },
];

export interface TestSentence {
  text: string;
  /** what must come out – every listed field of the first item of that kind */
  expect: {
    kind: MagicItem['kind'];
    count?: number;
    start?: string;
    who?: string[];
    freq?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    days?: number[];
    skipHolidays?: boolean;
  }[];
}

/** Sentences as families say them – typed, and spoken with the usual slips. */
export const TEST_SENTENCES: TestSentence[] = [
  {
    text: 'Lotta hat nächsten Dienstag um 15 Uhr Zahnarzt, Impfpass mitnehmen',
    expect: [{ kind: 'event', start: '15:00', who: ['Lotta'] }],
  },
  {
    text: 'Ben hat jeden Dienstag um 17 Uhr Fußball, nicht in den Ferien',
    expect: [{ kind: 'event', start: '17:00', freq: 'weekly', days: [2], skipHolidays: true }],
  },
  {
    text: 'Jonas Klavier montags und mittwochs um halb vier',
    expect: [{ kind: 'event', start: '15:30', freq: 'weekly', days: [1, 3] }],
  },
  {
    text: 'Elternstammtisch jeden ersten Donnerstag im Monat um 20 Uhr',
    expect: [{ kind: 'event', start: '20:00', freq: 'monthly' }],
  },
  {
    text: 'Oma hat am 12. März Geburtstag, jedes Jahr',
    expect: [{ kind: 'event', freq: 'yearly' }],
  },
  {
    text: 'Wir brauchen Milch, Eier und zwei Kilo Kartoffeln',
    expect: [{ kind: 'shopping', count: 3 }],
  },
  {
    text: 'Geschenk für Oma besorgen bis Freitag',
    expect: [{ kind: 'todo' }],
  },
  // 1.28: "Aufgabe" is the app's word for both – once without stars stays a todo
  {
    text: 'Aufgabe: Geschenk für Oma besorgen',
    expect: [{ kind: 'todo' }],
  },
  {
    text: 'Neue Aufgabe für Papa: Reifen wechseln bis Samstag',
    expect: [{ kind: 'todo', who: ['Papa'] }],
  },
  {
    text: 'Aufgabe für Jonas: jeden Samstag den Müll rausbringen, 5 Sterne',
    expect: [{ kind: 'task', who: ['Jonas'] }],
  },
  {
    text: 'Jonas soll morgen einmal das Auto waschen, 10 Sterne',
    expect: [{ kind: 'task', who: ['Jonas'] }],
  },
  {
    text: 'Lotta soll jeden Tag den Hund füttern, drei Sterne',
    expect: [{ kind: 'task', who: ['Lotta'] }],
  },
  {
    // Mike, 26.10.: a day without "Notiz" may well become an event
    text: 'Oma kommt am Sonntag zum Kaffee',
    expect: [{ kind: 'event' }],
  },
  {
    text: 'Notiz für alle: der Schlüssel liegt beim Nachbarn',
    expect: [{ kind: 'note' }],
  },
  {
    text: 'Wir wollten mal in den Kletterpark',
    expect: [{ kind: 'idea' }],
  },
  {
    text: 'Lotta wünscht sich ein Fahrrad',
    expect: [{ kind: 'wish', who: ['Lotta'] }],
  },
  // spoken: names misheard, times in words
  {
    text: 'lote hat morgen um viertel nach fünf ballett',
    expect: [{ kind: 'event', start: '17:15', who: ['Lotta'] }],
  },
  {
    text: 'ben hat am mittwoch um drei uhr nachmittags kinderarzt',
    expect: [{ kind: 'event', start: '15:00', who: ['Ben'] }],
  },
  {
    text: 'jonas training immer dienstags und donnerstags von sechs bis halb acht abends',
    expect: [{ kind: 'event', start: '18:00', freq: 'weekly', days: [2, 4] }],
  },
  {
    text: 'Elternabend Mittwoch 19 Uhr und wir brauchen noch Butter',
    expect: [{ kind: 'event', start: '19:00' }, { kind: 'shopping' }],
  },
];

type Loose = Record<string, unknown> & { kind: string };

/** Did the AI get the sentence right? Every expected field must match; extra items are fine. */
export function checkSentence(t: TestSentence, items: Loose[]): boolean {
  return t.expect.every((e) => {
    const same = items.filter((i) => i.kind === e.kind);
    if (!same.length) return false;
    if (e.count !== undefined && same.length !== e.count) return false;
    const i = same[0]!;
    if (e.start !== undefined && i.start !== e.start) return false;
    if (
      e.who &&
      JSON.stringify([...((i.who as string[]) ?? [])].sort()) !== JSON.stringify([...e.who].sort())
    )
      return false;
    const r = i.repeat as { freq?: string; days?: number[]; skipHolidays?: boolean } | undefined;
    if (e.freq && (r?.freq ?? (r ? 'weekly' : undefined)) !== e.freq) return false;
    if (e.days && JSON.stringify(r?.days ?? []) !== JSON.stringify(e.days)) return false;
    if (e.skipHolidays !== undefined && Boolean(r?.skipHolidays) !== e.skipHolidays) return false;
    return true;
  });
}

/** One measured call as stored in aiCalls. */
export interface AiCall {
  task: AiTask;
  model: string;
  attempts: number;
  ms: number;
  tokensIn: number;
  tokensOut: number;
  ok: boolean;
  at: number;
  test?: boolean;
}

export interface AiTaskStats {
  task: AiTask;
  calls: number;
  /** answered in the first attempt, share 0–1 */
  firstTry: number;
  failed: number;
  avgMs: number;
  p90Ms: number;
  costCents: number;
}

/** The admin's measuring table: per job, the calls of the last `days` days (tests left out). */
export function aiStats(rows: AiCall[], now: number, days: number): AiTaskStats[] {
  const from = now - days * 86_400_000;
  return AI_TASKS.map((task) => {
    const r = rows.filter((x) => x.task === task && !x.test && x.at >= from);
    const ms = r.map((x) => x.ms).sort((a, b) => a - b);
    return {
      task,
      calls: r.length,
      firstTry: r.length ? r.filter((x) => x.ok && x.attempts === 1).length / r.length : 0,
      failed: r.filter((x) => !x.ok).length,
      avgMs: ms.length ? Math.round(ms.reduce((a, b) => a + b, 0) / ms.length) : 0,
      p90Ms: ms.length ? ms[Math.min(ms.length - 1, Math.floor(ms.length * 0.9))]! : 0,
      costCents: r.reduce((a, x) => a + costCents(x.model, x.tokensIn, x.tokensOut), 0),
    };
  });
}
